import React, { useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks } from '../../utils/chartResponsive';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import EmptyState from '../ui/EmptyState';

interface ComparisonData {
  month: string;
  branch?: {
    sales: number;
    invoices: number;
  };
  brand?: {
    sales: number;
    invoices: number;
  };
  all?: {
    sales: number;
    invoices: number;
  };
}

interface ComparisonChartProps {
  data: ComparisonData[];
  currencySymbol: string;
  formatKpiValue: (value: number | string, type?: 'currency' | 'percentage' | 'number') => string;
  // chartRef : useRef<HTMLDivElement>(null);
  chartRef?: React.RefObject<HTMLDivElement>;
  onInsightTypeChange?: (type: 'comparison-invoice' | 'comparison-normalized' | 'comparison-actual') => void;
}

const ComparisonChart: React.FC<ComparisonChartProps> = ({
  data,
  currencySymbol,
  formatKpiValue,
  chartRef,
  onInsightTypeChange,
}) => {
  // const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const [showNormalizedComparison, setShowNormalizedComparison] = useState(true);
  const [showInvoiceComparison, setShowInvoiceComparison] = useState(false);

  // Notify parent about insight type changes
  React.useEffect(() => {
    if (onInsightTypeChange) {
      if (showInvoiceComparison) {
        onInsightTypeChange('comparison-invoice');
      } else if (showNormalizedComparison) {
        onInsightTypeChange('comparison-normalized');
      } else {
        onInsightTypeChange('comparison-actual');
      }
    }
  }, [showInvoiceComparison, showNormalizedComparison, onInsightTypeChange]);

  if (data.length === 0) {
    return <EmptyState />;
  }

  const renderChart = () => {
    if (showInvoiceComparison) {
      // Invoice count comparison
      return (
        <Line
          data={{
            labels: data.map(item => item.month || ''),
            datasets: [
              {
                label: 'شعبه فعلی',
                data: data.map(item => item.branch?.invoices || 0),
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                tension: 0.3,
                fill: false,
              },
              {
                label: 'میانگین برند',
                data: data.map(item => item.brand?.invoices || 0),
                borderColor: getChartColors(currentTheme)[1],
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[1],
                tension: 0.3,
                fill: false,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                position: 'bottom' as const,
                display: true,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    const separator = '\u00A0:\u00A0';
                    const formattedValue = `${formatKpiValue(value)} سفارش`;
                    return `${label}${separator}${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return formatKpiValue(value);
                  }
                },
                title: {
                  display: true,
                  text: 'تعداد سفارش (ترافیک مشتریان)',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
              },
            }
          }}
        />
      );
    } else if (showNormalizedComparison) {
      // Normalized trend comparison
      const branchSales = data.map(item => item.branch?.sales || 0);
      const brandSales = data.map(item => item.brand?.sales || 0);
      
      const normalizeSeries = (values: number[]) => {
        const maxVal = Math.max(...values);
        const minVal = Math.min(...values);
        if (maxVal === minVal) return values.map(() => 50);
        return values.map(val => ((val - minVal) / (maxVal - minVal)) * 100);
      };
      
      const normalizedBranch = normalizeSeries(branchSales);
      const normalizedBrand = normalizeSeries(brandSales);
      
      return (
        <Line
          data={{
            labels: data.map(item => item.month || ''),
            datasets: [
              {
                label: 'شعبه فعلی (روند)',
                data: normalizedBranch,
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                tension: 0.3,
                fill: false,
              },
              {
                label: 'میانگین برند (روند)',
                data: normalizedBrand,
                borderColor: getChartColors(currentTheme)[1],
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[1],
                tension: 0.3,
                fill: false,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                position: 'bottom' as const,
                display: true,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const normalizedValue = context.parsed.y;
                    
                    const separator = '\u00A0:\u00A0';
                    const formattedValue = `${normalizedValue.toFixed(1)}%`;
                    return `${label}${separator}${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                min: 0,
                max: 110,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return value <= 100 ? `${value}%` : '';
                  },
                  stepSize: 20,
                },
                title: {
                  display: true,
                  text: 'روند نرمال‌شده (درصد از حداکثر هر سری)',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
              },
            }
          }}
        />
      );
    } else {
      // Actual values comparison
      return (
        <Line
          data={{
            labels: data.map(item => item.month || ''),
            datasets: [
              {
                label: 'شعبه فعلی',
                data: data.map(item => item.branch?.sales || 0),
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                tension: 0.3,
                fill: false,
              },
              {
                label: 'میانگین برند',
                data: data.map(item => item.brand?.sales || 0),
                borderColor: getChartColors(currentTheme)[1],
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[1],
                tension: 0.3,
                fill: false,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                position: 'bottom' as const,
                display: true,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    const separator = '\u00A0:\u00A0';
                    const formattedValue = `${formatKpiValue(value, 'currency')} ${currencySymbol}`;
                    return `${label}${separator}${formattedValue}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return formatKpiValue(value, 'currency');
                  }
                },
                title: {
                  display: true,
                  text: `مبلغ فروش (${currencySymbol})`,
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
              },
            }
          }}
        />
      );
    }
  };

  return (
    <div ref={chartRef}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
        <h3 className="title-card !mb-0">مقایسه روند شعبه، برند</h3>
        <div className="sales-trend-toggle-row lg:flex bg-bg-secondary rounded-lg p-1">
          <button
            onClick={() => {
              setShowNormalizedComparison(false);
              setShowInvoiceComparison(false);
            }}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              !showNormalizedComparison && !showInvoiceComparison
                ? 'bg-bg-primary text-text-primary shadow-sm' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            فروش
          </button>
          <button
            onClick={() => {
              setShowNormalizedComparison(false);
              setShowInvoiceComparison(true);
            }}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              !showNormalizedComparison && showInvoiceComparison
                ? 'bg-bg-primary text-text-primary shadow-sm' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            تعداد سفارش
          </button>
          <button
            onClick={() => {
              setShowNormalizedComparison(true);
              setShowInvoiceComparison(false);
            }}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              showNormalizedComparison && !showInvoiceComparison
                ? 'bg-bg-primary text-text-primary shadow-sm' 
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            روند نرمال شده
          </button>
        </div>
      </div>
      <div className="mobile-chart-host" style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار مقایسه روند فروش">
        {renderChart()}
      </div>
    </div>
  );
};

export default ComparisonChart;